import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Users, Check, X, Loader2, Merge, ShieldAlert, Radio } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useDuplicates, useMergeContacts, useDismissDuplicate } from '@/hooks/use-contacts'
import type { DuplicateContact, DuplicateGroup, MergePayload } from '@/lib/types'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#84cc16',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#3b82f6',
  '#0F2E28',
  '#FF6F61',
  '#a855f7',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
]

function hashName(name: string): number {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash)
}

function avatarColor(c: DuplicateContact): string {
  const name = `${c.firstName}${c.lastName ?? ''}`
  return AVATAR_COLORS[hashName(name) % AVATAR_COLORS.length]
}

function initials(c: DuplicateContact): string {
  const first = c.firstName?.charAt(0) ?? ''
  const last = c.lastName?.charAt(0) ?? ''
  return (first + last).toUpperCase() || '?'
}

function fullName(c: DuplicateContact): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ')
}

function confidenceLabel(confidence: number): { label: string; className: string } {
  if (confidence >= 70) return { label: 'High', className: 'tag-coral' }
  if (confidence >= 40) return { label: 'Medium', className: 'tag-chartreuse' }
  return { label: 'Low', className: 'tag-lilac' }
}

// Merge-relevant fields for field-by-field comparison
const MERGE_FIELDS = ['firstName', 'lastName', 'email', 'phone', 'company'] as const
type MergeField = (typeof MERGE_FIELDS)[number]

const FIELD_LABELS: Record<MergeField, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  phone: 'Phone',
  company: 'Company',
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

function Avatar({ contact, size = 'md' }: { contact: DuplicateContact; size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm'
  if (contact.avatarUrl) {
    return (
      <img
        src={contact.avatarUrl}
        alt={fullName(contact)}
        className={cn('shrink-0 rounded-full object-cover', dim)}
      />
    )
  }
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-semibold text-white',
        dim,
      )}
      style={{ backgroundColor: avatarColor(contact) }}
    >
      {initials(contact)}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Duplicate Group Card
// ---------------------------------------------------------------------------

function DuplicateGroupCard({
  group,
  onMerge,
  onDismiss,
  isDismissing,
}: {
  group: DuplicateGroup
  onMerge: (group: DuplicateGroup) => void
  onDismiss: (group: DuplicateGroup) => void
  isDismissing: boolean
}) {
  const { t } = useTranslation()
  const badge = confidenceLabel(group.confidence)

  return (
    <section className="bg-surface-container-low rounded-xl p-4">
      {/* Header with confidence badge */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="text-on-surface-variant size-4" />
          <span className="text-sm font-medium">
            {group.contacts.length} {t('duplicates.possibleDuplicates')}
          </span>
        </div>
        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', badge.className)}>
          {badge.label}
        </span>
      </div>

      {/* Matched reasons */}
      <p className="text-on-surface-variant mb-3 text-xs">
        {t('duplicates.matchedBy')}: {group.reasons.join(', ')}
      </p>

      {/* Contact cards side-by-side */}
      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {group.contacts.map((contact) => (
          <div
            key={contact.id}
            className={cn(
              'flex items-center gap-3 rounded-md border p-3',
              contact.id === group.suggestedPrimaryId && 'border-primary/50 bg-primary/5',
            )}
          >
            <Avatar contact={contact} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{fullName(contact)}</p>
              {contact.email && (
                <p className="text-on-surface-variant truncate text-xs">{contact.email}</p>
              )}
              {contact.phone && (
                <p className="text-on-surface-variant truncate text-xs">{contact.phone}</p>
              )}
              {contact.company && (
                <p className="text-on-surface-variant truncate text-xs">{contact.company}</p>
              )}
            </div>
            {contact.id === group.suggestedPrimaryId && (
              <span className="bg-primary/10 text-primary shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium">
                {t('duplicates.suggested')}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onMerge(group)} className="flex-1">
          <Merge className="mr-1.5 size-3.5" />
          {t('duplicates.merge')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDismiss(group)}
          disabled={isDismissing}
        >
          {isDismissing ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <X className="size-3.5" />
          )}
          <span className="ml-1.5">{t('duplicates.notDuplicates')}</span>
        </Button>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Merge Dialog
// ---------------------------------------------------------------------------

type MergeStrategy = 'keep_recent' | 'keep_primary' | 'manual'

function MergeDialog({ group, onClose }: { group: DuplicateGroup; onClose: () => void }) {
  const { t } = useTranslation()
  const merge = useMergeContacts()

  const [primaryId, setPrimaryId] = useState(group.suggestedPrimaryId)
  const [strategy, setStrategy] = useState<MergeStrategy>('keep_recent')
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, 'primary' | 'secondary'>>({})

  const primary = group.contacts.find((c) => c.id === primaryId)!
  const secondary = group.contacts.filter((c) => c.id !== primaryId)

  function handleFieldOverride(field: string, value: 'primary' | 'secondary') {
    setFieldOverrides((prev) => ({ ...prev, [field]: value }))
  }

  function handleMerge() {
    const payload: MergePayload = {
      primaryContactId: primaryId,
      secondaryContactIds: secondary.map((c) => c.id),
      strategy,
    }
    if (strategy === 'manual' && Object.keys(fieldOverrides).length > 0) {
      payload.fieldOverrides = fieldOverrides
    }
    merge.mutate(payload, {
      onSuccess: () => onClose(),
    })
  }

  const strategies: { value: MergeStrategy; label: string; icon: typeof Check }[] = [
    { value: 'keep_recent', label: t('duplicates.strategyRecent'), icon: Check },
    { value: 'keep_primary', label: t('duplicates.strategyPrimary'), icon: ShieldAlert },
    { value: 'manual', label: t('duplicates.strategyManual'), icon: Radio },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="bg-background relative z-10 flex max-h-[85dvh] w-full max-w-lg flex-col rounded-t-2xl sm:rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold">{t('duplicates.mergeContacts')}</h2>
          <button onClick={onClose} className="hover:bg-surface-container-high rounded-full p-1">
            <X className="size-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {/* Select primary contact */}
          <div>
            <h3 className="mb-2 text-sm font-medium">{t('duplicates.selectPrimary')}</h3>
            <div className="space-y-2">
              {group.contacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => setPrimaryId(contact.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                    contact.id === primaryId
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-surface-container-high/50',
                  )}
                >
                  <Avatar contact={contact} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{fullName(contact)}</p>
                    <p className="text-on-surface-variant truncate text-xs">
                      {[contact.email, contact.phone].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  {contact.id === primaryId && <Check className="text-primary size-4 shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          {/* Strategy */}
          <div>
            <h3 className="mb-2 text-sm font-medium">{t('duplicates.mergeStrategy')}</h3>
            <div className="space-y-1.5">
              {strategies.map((s) => {
                const Icon = s.icon
                return (
                  <button
                    key={s.value}
                    onClick={() => setStrategy(s.value)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                      strategy === s.value
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-surface-container-high/50',
                    )}
                  >
                    <Icon className="text-on-surface-variant size-4 shrink-0" />
                    <span>{s.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Field-by-field comparison (always shown, editable in manual mode) */}
          <div>
            <h3 className="mb-2 text-sm font-medium">{t('duplicates.fieldComparison')}</h3>
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-container-high/50 border-b">
                    <th className="px-3 py-2 text-left font-medium">{t('duplicates.field')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('duplicates.primary')}</th>
                    {secondary.map((c) => (
                      <th key={c.id} className="px-3 py-2 text-left font-medium">
                        {fullName(c)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MERGE_FIELDS.map((field) => {
                    const primaryVal = primary[field] ?? '—'
                    return (
                      <tr key={field} className="border-b last:border-b-0">
                        <td className="text-on-surface-variant px-3 py-2">{FIELD_LABELS[field]}</td>
                        <td className="px-3 py-2">
                          {strategy === 'manual' ? (
                            <label className="flex cursor-pointer items-center gap-1.5">
                              <input
                                type="radio"
                                name={`field-${field}`}
                                checked={fieldOverrides[field] !== 'secondary'}
                                onChange={() => handleFieldOverride(field, 'primary')}
                                className="accent-primary"
                              />
                              <span
                                className={cn(
                                  fieldOverrides[field] !== 'secondary' && 'font-medium',
                                )}
                              >
                                {primaryVal}
                              </span>
                            </label>
                          ) : (
                            <span>{primaryVal}</span>
                          )}
                        </td>
                        {secondary.map((c) => {
                          const secVal = c[field] ?? '—'
                          return (
                            <td key={c.id} className="px-3 py-2">
                              {strategy === 'manual' ? (
                                <label className="flex cursor-pointer items-center gap-1.5">
                                  <input
                                    type="radio"
                                    name={`field-${field}`}
                                    checked={fieldOverrides[field] === 'secondary'}
                                    onChange={() => handleFieldOverride(field, 'secondary')}
                                    className="accent-primary"
                                  />
                                  <span
                                    className={cn(
                                      fieldOverrides[field] === 'secondary' && 'font-medium',
                                    )}
                                  >
                                    {secVal}
                                  </span>
                                </label>
                              ) : (
                                <span>{secVal}</span>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 border-t px-4 py-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {t('contacts.cancel')}
          </Button>
          <Button onClick={handleMerge} disabled={merge.isPending} className="flex-1">
            {merge.isPending ? (
              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
            ) : (
              <Merge className="mr-1.5 size-3.5" />
            )}
            {merge.isPending ? t('duplicates.merging') : t('duplicates.confirmMerge')}
          </Button>
        </div>

        {merge.isError && (
          <p className="text-destructive px-4 pb-3 text-sm">{t('duplicates.mergeError')}</p>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

function GroupSkeleton() {
  return (
    <section className="bg-surface-container-low animate-pulse rounded-xl p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="bg-surface-container-high h-4 w-32 rounded" />
        <div className="bg-surface-container-high h-5 w-14 rounded-full" />
      </div>
      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {[0, 1].map((i) => (
          <div key={i} className="flex items-center gap-3 rounded-md border p-3">
            <div className="bg-surface-container-high h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <div className="bg-surface-container-high h-4 w-24 rounded" />
              <div className="bg-surface-container-high h-3 w-32 rounded" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <div className="bg-surface-container-high h-8 flex-1 rounded" />
        <div className="bg-surface-container-high h-8 w-28 rounded" />
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function DuplicatesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, isLoading, isError } = useDuplicates()
  const dismiss = useDismissDuplicate()
  const [mergeGroup, setMergeGroup] = useState<DuplicateGroup | null>(null)
  const [dismissingGroupIdx, setDismissingGroupIdx] = useState<number | null>(null)

  function handleDismiss(group: DuplicateGroup, idx: number) {
    if (group.contacts.length < 2) return
    setDismissingGroupIdx(idx)
    // Dismiss all pairs in the group
    const pairs: { contactId1: string; contactId2: string }[] = []
    for (let i = 0; i < group.contacts.length; i++) {
      for (let j = i + 1; j < group.contacts.length; j++) {
        pairs.push({ contactId1: group.contacts[i].id, contactId2: group.contacts[j].id })
      }
    }
    // Dismiss sequentially — last one triggers refetch
    let chain = Promise.resolve<unknown>(undefined)
    for (const pair of pairs) {
      chain = chain.then(() => dismiss.mutateAsync(pair))
    }
    chain.finally(() => setDismissingGroupIdx(null))
  }

  const groups = data?.groups ?? []

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b px-4 py-3">
        <button
          onClick={() => navigate('/settings')}
          className="hover:bg-surface-container-high rounded-full p-1"
        >
          <ArrowLeft className="size-5" />
        </button>
        <h1 className="font-heading text-lg font-semibold">{t('duplicates.title')}</h1>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-6 pt-6 pb-8">
        {isLoading && (
          <>
            <GroupSkeleton />
            <GroupSkeleton />
          </>
        )}

        {isError && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <ShieldAlert className="text-on-surface-variant mb-3 size-10" />
            <p className="text-on-surface-variant text-sm">{t('duplicates.loadError')}</p>
          </div>
        )}

        {!isLoading && !isError && groups.length === 0 && (
          <EmptyState
            icon={Users}
            title={t('duplicates.noDuplicates')}
            description={t('duplicates.noDuplicatesHint')}
          />
        )}

        {groups.map((group, idx) => (
          <DuplicateGroupCard
            key={group.contacts.map((c) => c.id).join('-')}
            group={group}
            onMerge={setMergeGroup}
            onDismiss={(g) => handleDismiss(g, idx)}
            isDismissing={dismissingGroupIdx === idx}
          />
        ))}
      </div>

      {/* Merge Dialog */}
      {mergeGroup && <MergeDialog group={mergeGroup} onClose={() => setMergeGroup(null)} />}
    </div>
  )
}
